<script setup lang="ts">
const NsLogo = defineAsyncComponent(() => import('./NsLogo.vue'));
</script>

<template>
    <div
        class="relative min-h-[600px] lg:min-h-[750px] max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center mb-20">
        <!-- Left Section -->
        <div class="relative w-full lg:max-w-sm z-10">
            <!-- Background Gradient - Responsive -->
            <div class="w-[400px] h-[400px] sm:w-[600px] sm:h-[600px] lg:w-[800px] lg:h-[800px] 
                        rounded-full absolute -left-[200px] sm:-left-[400px] lg:-left-[650px] 
                        top-1/2 -translate-y-1/2 
                        bg-[linear-gradient(rgba(71,209,140,1),rgba(102,163,255,1))] 
                        blur-[150px] sm:blur-[200px] lg:blur-[300px] opacity-80 lg:opacity-100 -z-10">
            </div>
            <div>
                <!-- Circuit Image - Responsive positioning -->
                <div
                    class="absolute -left-8 -top-8 sm:-left-12 sm:-top-12 lg:-left-20 lg:-top-20 opacity-30 lg:opacity-100">
                    <NuxtImg src="/images/circuit.png" width="150" height="90"
                        class="sm:w-[200px] sm:h-[120px] lg:w-[298px] lg:h-[180px]" alt="Circuit Background"  loading="lazy" />
                </div>

                <!-- Icon Container -->
                <div class="flex items-center justify-center w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 
                        rounded-xl lg:rounded-2xl mb-4 sm:mb-5 lg:mb-6 
                        bg-[image:var(--primary-gradient)]">
                        <NsLogo name="NsVaahStore"  class="w-8 h-8 lg:w-10 lg:h-10" />
                </div>

                <!-- Main Heading - Responsive -->
                <h1 class="text-2xl sm:text-3xl lg:text-3xl max-w-full sm:max-w-[300px] lg:max-w-[250px] 
                       font-bold text-[var(--text-primary)] mb-4 sm:mb-5 lg:mb-6 leading-tight">
                    A Backend Built for Innovation
                </h1>

                <!-- Description - Responsive -->
                <p class="max-w-full sm:max-w-lg lg:max-w-4xl text-base sm:text-lg lg:text-xl 
               leading-6 sm:leading-7 lg:leading-8 text-[var(--text-gray)] mb-6 lg:mb-6">
                    Our <span class="text-blue-400 font-semibold">open-source</span> backend equips you with the robust
                    foundation every e-commerce platform needs to thrive. Key features include
                </p>

                <!-- CTA Button - Responsive -->
                <div class="mb-8 lg:mb-0">
                    <Button unstyled label="Learn More" iconPos="left" class="group !rounded-xl !px-4 sm:!px-5 
         bg-[image:var(--primary-gradient)] hover:bg-[image:var(--primary-gradient-reverse)]
         transition-all duration-1000 ease-in-out
         !py-2.5 sm:!py-3 font-semibold
         text-[var(--text-secondary)] 
         flex items-center justify-center gap-2 sm:gap-3 
         w-full sm:w-auto text-sm sm:text-base overflow-hidden">

                        <span class="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 
             rounded-full bg-white 
             transform transition-all duration-1000 ease-in-out 
             group-hover:translate-x-28">
                            <i class="pi pi-angle-right text-green-500 font-bold text-sm sm:text-lg"></i>
                        </span>
                        <span class="transition-all duration-1000 ease-in-out group-hover:-translate-x-1/2">
                            Learn More
                        </span>
                    </Button>
                </div>
            </div>

        </div>

        <!-- Background Orbit - Desktop Only (Hidden on Mobile/Tablet) -->
        <div aria-hidden="true" class="hidden xl:block absolute inset-0 overflow-hidden">
            <div class="absolute top-1/2 -translate-y-1/2 -left-[30%]">
                <!-- Ring 4 -->
                <div
                    class="absolute w-[2714px] h-[2714px] -left-[1130px] -top-[1357px] rounded-full border border-white/10">
                    <!-- Abandoned Cart -->
                    <div class="absolute group cursor-pointer top-[1150px] -right-[60px] 
     w-[130px] h-[130px] rounded-full bg-gray-800 
     flex items-center justify-end
     transition-all duration-500 hover:w-[350px] ease-in-out 
     text-[var(--text-primary)] font-semibold text-sm overflow-hidden">

                        <!-- Description (hidden until hover, expands on left) -->
                        <p class="opacity-0 max-w-0 overflow-hidden text-left
       group-hover:opacity-100 group-hover:max-w-[200px] min-w-[200px]
       transition-all duration-500 ease-in-out text-sm font-normal">
                            Recover lost sales with automated reminders.
                        </p>

                        <!-- Icon & Title (always visible on right) -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 mr-4">
                            <NsLogo name="NsAbandonedCartIcon" class="w-8 h-8" />
                            <span class="text-sm max-w-24 text-center">Abandoned Cart</span>
                        </div>
                    </div>


                    <!-- Brand Store -->
                    <div class="absolute group cursor-pointer top-[1430px] -right-[60px] 
     w-[130px] h-[130px] rounded-full bg-gray-800 
     flex items-center justify-end
     transition-all duration-500 hover:w-[350px] ease-in-out 
     text-[var(--text-primary)] font-semibold text-sm overflow-hidden">

                        <!-- Description (hidden until hover, expands left) -->
                        <p class="opacity-0 max-w-0 overflow-hidden text-left
       group-hover:opacity-100 group-hover:max-w-[200px] min-w-[200px]
       transition-all duration-500 ease-in-out text-sm font-normal mr-4">
                            Showcase and manage brands with dedicated store pages.
                        </p>

                        <!-- Icon & Title (always visible on right) -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 mr-6">
                            <NsLogo name="NsBrandStoreIcon" class="w-8 h-8" />
                            <span class="text-sm">Brand Store</span>
                        </div>
                    </div>

                </div>

                <!-- Ring 3 -->
                <div
                    class="absolute w-[2450px] h-[2450px] -left-[1050px] -top-[1225px] rounded-full border border-white/10">
                    <!-- Wishlist -->
                    <div class="absolute group cursor-pointer top-[1158px] left-[2380px] 
                         w-[130px] h-[130px] rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[330px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden z-50">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 ml-10 mr-6">
                            <NsLogo name="NsWishlistIcon" class="w-8 h-8" />

                            <span class="text-sm">Wishlist</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Let customers save products for later.
                        </p>
                    </div>

                    <!-- Dropship -->
                    <div class="absolute group cursor-pointer top-[1435px] left-[2355px] 
                         w-[130px] h-[130px] rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[350px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 ml-9 mr-10">
                            <NsLogo name="NsDropShipIcon" class="w-8 h-8" />
                            <span class="text-sm">Dropship</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Expand inventory without stock investment.
                        </p>
                    </div>

                    <!-- Multi-Currency Taxes -->
                    <div class="absolute group cursor-pointer top-[880px] left-[2355px] 
                         w-[130px] h-[130px] rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[350px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 mx-4">
                            <NsLogo name="NsMultiCountryTaxIcon" class="w-8 h-8" />
                            <span class="text-sm max-w-24 text-center">Multi-Country Taxes</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Simplify tax compliance across borders.
                        </p>
                    </div>
                </div>

                <!-- Ring 2 -->
                <div
                    class="absolute w-[1998px] h-[1998px] -left-[770px] -top-[999px] rounded-full border border-white/10">
                </div>

                <!-- Ring 1 -->
                <div
                    class="absolute w-[1552px] h-[1552px] -left-[500px] -top-[776px] rounded-full border border-white/10">
                    <!-- Multi-Lingual -->
                    <div class="absolute group cursor-pointer top-[800px] left-[1480px] 
                         w-[130px] h-[130px] rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[350px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 mx-5">
                            <NsLogo name="NsMultiLingualIcon" class="w-8 h-8" />
                            <span class="text-sm">Multi-Lingual</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Reach customers in their preferred languages.
                        </p>
                    </div>

                    <!-- Coupons -->
                    <div class="absolute group cursor-pointer top-[1020px] left-[1425px] 
                         w-[130px] h-[130px]  rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[350px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center  gap-2 justify-center shrink-0 ml-10 mr-10">
                            <NsLogo name="NsCouponsIcon" class="w-8 h-8" />
                            <span class="text-sm">Coupons</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Drive sales and customer loyalty with discount coupons and special offers.
                        </p>
                    </div>

                    <!-- Multi-Currency (Original) -->
                    <div class="absolute group cursor-pointer top-[600px] left-[1472px] 
                         w-[130px] h-[130px] rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[350px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 mx-4">
                            <NsLogo name="NsMultiCurrencyIcon" class="w-8 h-8" />
                            <span class="text-sm">Multi-Currency</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Support multiple currencies to cater to a global audience.
                        </p>
                    </div>

                    <!-- Multi-Vendor -->
                    <div class="absolute group cursor-pointer top-[400px] left-[1420px] 
                         w-[130px] h-[130px] rounded-full bg-gray-800 
                         flex items-center 
                         transition-all duration-500 hover:w-[350px] ease-in-out 
                         text-[var(--text-primary)] font-semibold text-sm overflow-hidden">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center gap-2 justify-center shrink-0 mx-5">
                            <NsLogo name="NsMultiVendorIcon" class="w-8 h-8" />
                            <span class="text-sm">Multi-Vendor</span>
                        </div>
                        <!-- Description (fade + slide in) -->
                        <p class="opacity-0 min-w-[200px] max-w-0 overflow-hidden
                           group-hover:opacity-100 group-hover:max-w-[200px]
                           transition-all duration-500 ease-in-out text-sm font-normal">
                            Easily manage multiple vendors with individual stores.
                        </p>
                    </div>
                </div>

            </div>
        </div>

        <!-- Mobile/Tablet Features Grid (Replaces orbit on smaller screens) -->
        <div class="xl:hidden mt-8 w-full">
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6 ml-2">
                <!-- Feature Cards -->
                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-server text-lg sm:text-xl text-blue-400 mb-2"></i>
                    <span
                        class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Multi-Lingual</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-database text-lg sm:text-xl text-green-400 mb-2"></i>
                    <span class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Database</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-lock text-lg sm:text-xl text-purple-400 mb-2"></i>
                    <span
                        class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Multi-Currency</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-bolt text-lg sm:text-xl text-yellow-400 mb-2"></i>
                    <span
                        class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Multi-Vendor</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-code text-lg sm:text-xl text-indigo-400 mb-2"></i>
                    <span class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Wishlist</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-desktop text-lg sm:text-xl text-cyan-400 mb-2"></i>
                    <span class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Dropship</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-cloud text-lg sm:text-xl text-rose-400 mb-2"></i>
                    <span class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Abandoned
                        Cart</span>
                </div>

                <div
                    class="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-white/10">
                    <i class="pi pi-chart-line text-lg sm:text-xl text-orange-400 mb-2"></i>
                    <span class="text-xs sm:text-sm font-medium text-[var(--text-primary)] text-center">Brand
                        Store</span>
                </div>
            </div>
        </div>
    </div>
</template>